import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, AlertCircle, ArrowRight, Building2, Home } from 'lucide-react';
import { API } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';

// Premium Image Collection - Bright Luxury Interior Images
// iter-315e: All Unsplash images now request w=2880&q=95 (4K, ultra-HD JPEG quality)
// and Pexels w=2880 for true full-screen sharpness on 2K/4K monitors.
const _UN = (url) => url.replace(/w=\d+&q=\d+/, 'w=2880&q=95');
const _PX = (url) => url.replace(/w=\d+/, 'w=2880');
const INTERIOR_IMAGES = [
  // Bright Luxury Living Rooms
  _UN('https://images.unsplash.com/photo-1758548157747-285c7012db5b?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1705326701287-346fc37a2c86?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1633505899118-4ca6bd143043?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1610659856580-323ec67011f9?w=1400&q=85'),
  _PX('https://images.pexels.com/photos/6899359/pexels-photo-6899359.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/6899354/pexels-photo-6899354.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/7546314/pexels-photo-7546314.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  // Luxury White Bathrooms
  _UN('https://images.unsplash.com/photo-1682817240020-f4f447888af6?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1721742736264-b39b85aeb6bc?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1735538497321-fcdb11d4b9f7?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1721742632944-de4309b888c3?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1596180737956-00cb917e382b?w=1400&q=85'),
  // Bright Luxury Bedrooms & Suites
  _UN('https://images.unsplash.com/photo-1590490359854-dfba19688d70?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1556020685-ae41abfc9365?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1593224647849-0ef96ecc2bdd?w=1400&q=85'),
  _PX('https://images.pexels.com/photos/6587902/pexels-photo-6587902.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/7587777/pexels-photo-7587777.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  // Modern Luxury Kitchens
  _UN('https://images.unsplash.com/photo-1758548157126-e4c0477f796e?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1758548157275-d939cf0f0e32?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1757262798620-ea10e2edd875?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1757262798639-fb6a5d2a752a?w=1400&q=85'),
  _PX('https://images.pexels.com/photos/7214450/pexels-photo-7214450.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/6969872/pexels-photo-6969872.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/6207816/pexels-photo-6207816.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  // Bright Grand Lobbies
  _UN('https://images.unsplash.com/photo-1760463921624-9af342c582a4?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1742844552699-2fb71ad66a72?w=1400&q=85'),
  // Luxury Penthouses & Panoramic Views
  _UN('https://images.unsplash.com/photo-1755868735286-82c86d784893?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1594850135327-57acc0317d05?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1590411255052-ea52fd44c531?w=1400&q=85'),
  _PX('https://images.pexels.com/photos/7031712/pexels-photo-7031712.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  // Bright Elegant Dining
  _PX('https://images.pexels.com/photos/7535046/pexels-photo-7535046.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  _PX('https://images.pexels.com/photos/6587894/pexels-photo-6587894.jpeg?auto=compress&cs=tinysrgb&w=1400'),
  // Sunlit Luxury Spaces
  _UN('https://images.unsplash.com/photo-1557207058-b39dff7b121f?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1743410975074-4be327c0ae63?w=1400&q=85'),
  _UN('https://images.unsplash.com/photo-1641851962761-43d3c1a34360?w=1400&q=85'),
];

// Get random image on each load (client-side randomized)
const getRandomImage = () => {
  const randomIndex = Math.floor(Math.random() * INTERIOR_IMAGES.length);
  return INTERIOR_IMAGES[randomIndex];
};

export default function Login({ signupMode = false } = {}) {
  const navigate = useNavigate();
  const { refreshTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(Boolean(signupMode));
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaType, setCaptchaType] = useState(''); // 'simple' or 'recaptcha'
  const [captchaSiteKey, setCaptchaSiteKey] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState(null); // For simple CAPTCHA
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
    organization: '',
  });

  // iter-318: Carousel — rotate background images every 6s for full-page slideshow
  useEffect(() => {
    setBackgroundImage(getRandomImage());
    const interval = setInterval(() => {
      setBackgroundImage(getRandomImage());
    }, 6000);
    return () => clearInterval(interval);
  }, []);
  
  // Load reCAPTCHA script when needed
  useEffect(() => {
    if (captchaRequired && captchaType === 'recaptcha' && captchaSiteKey && !window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [captchaRequired, captchaType, captchaSiteKey]);

  // Render reCAPTCHA when container is ready
  useEffect(() => {
    if (captchaRequired && captchaType === 'recaptcha' && captchaSiteKey && window.grecaptcha && window.grecaptcha.render) {
      const container = document.getElementById('recaptcha-container');
      if (container && !container.hasChildNodes()) {
        try {
          window.grecaptcha.render('recaptcha-container', {
            sitekey: captchaSiteKey,
            callback: (token) => setCaptchaToken(token),
            'expired-callback': () => setCaptchaToken(''),
          });
        } catch (e) {
          // Already rendered
        }
      }
    }
  }, [captchaRequired, captchaType, captchaSiteKey]);
  
  // Build CAPTCHA token for simple CAPTCHA
  useEffect(() => {
    if (captchaType === 'simple' && captchaChallenge && captchaAnswer) {
      setCaptchaToken(`${captchaChallenge.id}:${captchaAnswer}`);
    }
  }, [captchaType, captchaChallenge, captchaAnswer]);

  // Password validation
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('At least one lowercase letter');
    if (!/\d/.test(pwd)) errors.push('At least one number');
    return errors;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    if (isNewUser && newPassword) {
      setPasswordErrors(validatePassword(newPassword));
    } else {
      setPasswordErrors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Capture current form state before async operations (prevents stale closure)
    const currentFormData = { ...formData };
    const currentIsNewUser = isNewUser;
    
    // Validate password for new users
    if (currentIsNewUser && passwordErrors.length > 0) {
      toast.error('Please fix password requirements');
      return;
    }
    
    // Validate required fields for new users
    if (currentIsNewUser && !currentFormData.name?.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    // Check CAPTCHA if required
    if (captchaRequired) {
      if (captchaType === 'simple' && !captchaAnswer) {
        toast.error('Please answer the security question');
        return;
      }
      if (captchaType === 'recaptcha' && !captchaToken) {
        toast.error('Please complete the CAPTCHA verification');
        return;
      }
    }
    
    // Build captcha token for API
    const captchaTokenToSend = captchaType === 'simple' && captchaChallenge 
      ? `${captchaChallenge.id}:${captchaAnswer}` 
      : captchaToken;
    
    setLoading(true);

    try {
      if (currentIsNewUser) {
        // iter-386u-ap — public self-service signup. Single call returns
        // {token, user} and auto-lands the account on the Free plan with
        // a 7-day trial. No admin approval, no separate login round-trip.
        try {
          const signupResponse = await axios.post(`${API}/auth/signup`, {
            name: currentFormData.name,
            email: currentFormData.email,
            password: currentFormData.password,
          });
          localStorage.setItem('token', signupResponse.data.token);
          localStorage.setItem('user', JSON.stringify(signupResponse.data.user));
          refreshTheme();
          toast.success('Welcome to Bella Casa! Your free trial has started.');
          navigate('/');
        } catch (signupError) {
          const detail = signupError.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Sign-up failed');
          if (signupError.response?.status === 409) {
            toast.error(msg);
            setIsNewUser(false);
          } else {
            toast.error(msg);
          }
        }
      } else {
        // Existing user flow: Try to login
        try {
          const loginResponse = await axios.post(`${API}/auth/login`, { 
            email: currentFormData.email, 
            password: currentFormData.password,
            captcha_token: captchaTokenToSend || undefined
          });
          
          localStorage.setItem('token', loginResponse.data.token);
          localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
          
          // Clear CAPTCHA state on success
          setCaptchaRequired(false);
          setCaptchaToken('');
          
          // Refresh theme settings after login
          refreshTheme();
          
          toast.success('Welcome back!');
          navigate('/');
        } catch (loginError) {
          // Check if CAPTCHA is now required
          const errorDetail = loginError.response?.data?.detail;
          if (typeof errorDetail === 'object' && errorDetail.captcha_required) {
            setCaptchaRequired(true);
            setCaptchaType(errorDetail.captcha_type || 'simple');
            if (errorDetail.captcha_type === 'simple' && errorDetail.challenge) {
              setCaptchaChallenge(errorDetail.challenge);
              setCaptchaAnswer('');
            } else {
              setCaptchaSiteKey(errorDetail.site_key || '');
            }
            toast.warning(errorDetail.message || 'Please complete the security verification');
          } else if (loginError.response?.status === 401) {
            // Check if it has captcha info
            if (typeof errorDetail === 'object') {
              setCaptchaRequired(true);
              setCaptchaType(errorDetail.captcha_type || 'simple');
              if (errorDetail.captcha_type === 'simple' && errorDetail.challenge) {
                setCaptchaChallenge(errorDetail.challenge);
                setCaptchaAnswer('');
              }
              toast.warning(errorDetail.message);
            } else {
              // User doesn't exist, switch to signup mode
              setIsNewUser(true);
              toast.info('Account not found. Please enter your details to sign up.');
            }
          } else if (loginError.response?.status === 429) {
            // Too many attempts
            const detail = loginError.response?.data?.detail;
            if (typeof detail === 'object') {
              setCaptchaRequired(true);
              setCaptchaType(detail.captcha_type || 'simple');
              if (detail.captcha_type === 'simple' && detail.challenge) {
                setCaptchaChallenge(detail.challenge);
                setCaptchaAnswer('');
              } else {
                setCaptchaSiteKey(detail.site_key || '');
              }
              toast.warning(detail.message);
            } else {
              toast.error('Too many failed attempts. Please try again later.');
            }
          } else if (loginError.response?.status === 400) {
            // CAPTCHA verification failed
            toast.error(typeof errorDetail === 'string' ? errorDetail : 'Verification failed. Please try again.');
            // Get new challenge
            setCaptchaAnswer('');
            try {
              const captchaCheck = await axios.get(`${API}/auth/captcha-required?email=${currentFormData.email}`);
              if (captchaCheck.data.captcha_required && captchaCheck.data.challenge) {
                setCaptchaChallenge(captchaCheck.data.challenge);
              }
            } catch (e) {
              // Ignore
            }
          } else {
            toast.error(typeof errorDetail === 'string' ? errorDetail : 'Authentication failed');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* iter-318: Full-page 4K carousel background — covers entire viewport edge-to-edge */}
      <div
        className="fixed inset-0 bg-cover bg-center transition-all duration-1000"
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
          backgroundColor: '#292524',
        }}
        data-testid="login-background-image"
      />
      {/* Soft dark gradient — full-page (slightly darker on left for form readability) */}
      <div className="fixed inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />

      <div className="min-h-screen grid md:grid-cols-2 relative">
        {/* iter-318: Form on LEFT (frosted glass panel) */}
        <div className="flex items-center justify-center p-8 md:p-12 relative order-1">
          <div className="w-full max-w-md bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/40">
          {/* Mobile Logo */}
          <div className="md:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="text-stone-800 font-medium tracking-wide">BELLA CASA</span>
          </div>

          {/* Header */}
          <div className="mb-10">
            <h2 className="font-playfair text-3xl lg:text-4xl font-bold text-stone-900 mb-3">
              {isNewUser ? 'Create Account' : 'Welcome to Bella Casa'}
            </h2>
            <p className="text-stone-500 text-base">
              {isNewUser 
                ? 'Enter your details to get started' 
                : 'One system. Complete project visibility.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Name Field - Only for new users */}
            {isNewUser && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-stone-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required={isNewUser}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="name-input"
                />
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
              <input
                type="email"
                required
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="email-input"
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-stone-700">Password</label>
                {!isNewUser && (
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-amber-600 hover:text-amber-700 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white pr-12"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {/* Password Requirements for New Users */}
              {isNewUser && formData.password && (
                <div className="mt-3 p-4 bg-stone-100 rounded-lg animate-fadeIn">
                  <p className="text-xs font-medium text-stone-600 mb-2">Password requirements:</p>
                  <ul className="space-y-1.5">
                    {['At least 8 characters', 'At least one uppercase letter', 'At least one lowercase letter', 'At least one number'].map((req, idx) => {
                      const passed = !passwordErrors.includes(req);
                      return (
                        <li key={idx} className={`text-xs flex items-center ${passed ? 'text-green-600' : 'text-stone-400'}`}>
                          {passed ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                          )}
                          {req}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Role Selection - Only for new users */}
            {isNewUser && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-stone-700 mb-2">I am a</label>
                <select
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="role-select"
                >
                  <option value="client">Client / Property Owner</option>
                  <option value="staff">Staff (Admin Dashboard Access)</option>
                  <option value="architect">Architect</option>
                  <option value="interior_designer">Interior Designer</option>
                  <option value="consultant">Consultant</option>
                  <option value="vendor">Vendor / Contractor</option>
                </select>
                <p className="text-xs text-stone-400 mt-1">
                  {formData.role === 'vendor' ? 'You will get access to vendor tools' : 
                   formData.role === 'staff' ? 'You will get admin dashboard access' :
                   'You will get access to project tracking & AI tools'}
                </p>
              </div>
            )}

            {/* Organization Field - Only for new users (optional) */}
            {isNewUser && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Organization / Firm <span className="text-stone-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white"
                    placeholder="e.g., ABC Architects Pvt. Ltd."
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    data-testid="organization-input"
                  />
                </div>
              </div>
            )}

            {/* CAPTCHA - Shown after 3 failed login attempts */}
            {captchaRequired && (
              <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Security Verification
                </label>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-3">
                  <p className="text-sm text-amber-800">
                    Too many failed attempts. Please verify you&apos;re human.
                  </p>
                </div>
                
                {captchaType === 'simple' && captchaChallenge ? (
                  <div className="space-y-3">
                    <div className="bg-stone-100 rounded-lg p-4 text-center">
                      <p className="text-lg font-medium text-stone-900">{captchaChallenge.question}</p>
                    </div>
                    <input
                      type="number"
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-white text-center text-lg font-medium"
                      placeholder="Enter your answer"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      data-testid="captcha-answer-input"
                    />
                  </div>
                ) : captchaType === 'recaptcha' && captchaSiteKey ? (
                  <div id="recaptcha-container" className="flex justify-center"></div>
                ) : null}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (isNewUser && passwordErrors.length > 0) || (captchaRequired && captchaType === 'simple' && !captchaAnswer) || (captchaRequired && captchaType === 'recaptcha' && !captchaToken)}
              className="w-full py-3.5 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
              data-testid="submit-button"
            >
              {loading ? (
                <span>Please wait...</span>
              ) : (
                <>
                  <span>{isNewUser ? 'Create Account' : 'Continue'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* iter-318: Sign in with Google (Emergent-managed OAuth) */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="mt-4">
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 h-px bg-stone-200" />
              <span className="text-xs text-stone-400">or</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full py-3 bg-white hover:bg-stone-50 border border-stone-300 text-stone-800 font-medium rounded-lg transition-all flex items-center justify-center gap-3 shadow-sm"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.49 12c0-.73.13-1.43.35-2.1V7.07H2.18a11 11 0 0 0 0 9.87l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.3 9.14 5.38 12 5.38z"/>
              </svg>
              <span>Sign in with Google</span>
            </button>
          </div>

          {/* Toggle Mode */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsNewUser(!isNewUser);
                setPasswordErrors([]);
                setCaptchaRequired(false);
                setCaptchaToken('');
                setCaptchaType('');
                setCaptchaChallenge(null);
                setCaptchaAnswer('');
                if (!isNewUser) {
                  setFormData({ ...formData, name: '', role: 'client', organization: '' });
                }
              }}
              className="text-stone-500 hover:text-stone-700 transition-colors text-sm"
              data-testid="toggle-auth-mode"
            >
              {isNewUser ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>

          {/* Legal Links */}
          <div className="mt-10 pt-6 border-t border-stone-200">
            <div className="flex flex-wrap justify-center gap-3 text-xs text-stone-400">
              <a href="/legal/privacy" className="hover:text-amber-600 transition-colors">Privacy Policy</a>
              <span>•</span>
              <a href="/legal/terms" className="hover:text-amber-600 transition-colors">Terms & Conditions</a>
              <span>•</span>
              <a href="/legal/refund" className="hover:text-amber-600 transition-colors">Refund Policy</a>
              <span>•</span>
              <a href="/legal/contact" className="hover:text-amber-600 transition-colors">Contact Us</a>
            </div>
          </div>
        </div>
        </div>

        {/* iter-318: Right Side - Brand tagline overlay (edge-to-edge image visible through transparent panel) */}
        <div className="hidden md:flex relative flex-col justify-between p-12 pointer-events-none order-2">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500/90 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="text-white/95 font-medium tracking-wide drop-shadow-md">BELLA CASA</span>
            </div>
          </div>
          
          <div className="text-white">
            <h1 className="font-playfair text-4xl lg:text-5xl font-bold mb-4 leading-tight drop-shadow-lg">
              One system.<br />
              Complete project visibility.
            </h1>
            <p className="text-lg text-white/85 font-light max-w-md drop-shadow-md">
              Designed for seamless interior project execution
            </p>
            <div className="flex items-center gap-6 mt-8">
              <div className="flex items-center gap-2 text-white/80">
                <Building2 className="w-4 h-4" />
                <span className="text-sm">Commercial</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Home className="w-4 h-4" />
                <span className="text-sm">Residential</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
